import { createAsyncThunk } from "@reduxjs/toolkit";

import { apiClient } from "@/config/api";
import { handleApiError } from "@/utils/functions";

import {
  CreateCalendarBlockBody,
  GetCalendarBlocksBody,
  GetCalendarBlocksBodyYearOverview,
  GetTapeChartDataBody,
  GetPotentialRevenueParams,
} from "./types";
import { toDateISOString } from "@/utils/dateFormatters";

export const createCalendarBlock = createAsyncThunk(
  "create/calendar/block",
  async (
    { propertyId, body }: { propertyId: string; body: CreateCalendarBlockBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(
        `property/${propertyId}${
          body.unitId ? `/unit/${body.unitId}` : ""
        }/calendar-blocks`,
        {
          ...body,
          start: body.start ? toDateISOString(body.start) : null,
          end: body.end ? toDateISOString(body.end) : null,
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Create calendar block failed")
      );
    }
  }
);

export const createCalendarBlockForUnit = createAsyncThunk(
  "create/calendar/block/unit",
  async (
    {
      propertyId,
      unitId,
      body,
    }: {
      propertyId: string;
      unitId: string;
      body: CreateCalendarBlockBody;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(
        `property/${propertyId}/unit/${unitId}/calendar-blocks`,
        {
          ...body,
          start: body.start ? toDateISOString(body.start) : null,
          end: body.end ? toDateISOString(body.end) : null,
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Create calendar block for unit failed")
      );
    }
  }
);

export const getPropertyCalendarBlocks = createAsyncThunk(
  "/property/:id/calendar-blocks",
  async (
    { id, month, year, unitId }: GetCalendarBlocksBody,
    { rejectWithValue }
  ) => {
    try {
      const url = `/property/${id}${
        unitId ? `/unit/${unitId}` : ""
      }/calendar-blocks`;
      const response = await apiClient.get(url, {
        params: {
          year,
          month,
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get calendar blocks failed")
      );
    }
  }
);

export const updateCalendarBlock = createAsyncThunk(
  "update/calendar/block",
  async (
    {
      propertyId,
      blockId,
      body,
    }: { propertyId: string; blockId: string; body: CreateCalendarBlockBody },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.put(
        `property/${propertyId}${
          body.unitId ? `/unit/${body.unitId}` : ""
        }/calendar-block/${blockId}`,
        {
          ...body,
          start: body.start ? toDateISOString(body.start) : null,
          end: body.end ? toDateISOString(body.end) : null,
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Update calendar block failed")
      );
    }
  }
);

export const getPropertyCalendarBlocksYearOverview = createAsyncThunk(
  "/property/:id/calendar-blocks/year-overview",
  async (
    { id, year, unitId }: GetCalendarBlocksBodyYearOverview,
    { rejectWithValue }
  ) => {
    try {
      const url = `/property/${id}${
        unitId ? `/unit/${unitId}` : ""
      }/calendar-blocks/year-overview`;
      const response = await apiClient.get(url, {
        params: {
          year,
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get calendar blocks year overview failed")
      );
    }
  }
);

export const getTapeChartData = createAsyncThunk(
  "/tape-chart",
  async (
    {
      start,
      end,
      numberOfBedrooms,
      propertyIds,
      homeownerIds,
      propertyTagIds,
      page,
      cityStates,
    }: GetTapeChartDataBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(`tape-chart`, {
        params: {
          start,
          end,
          numberOfBedrooms,
          propertyIds,
          homeownerIds,
          propertyTagIds,
          page,
          cityStates,
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get tape chart data failed")
      );
    }
  }
);

export const getCityStates = createAsyncThunk(
  "/tape-chart/filter/property-locations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `tape-chart/filter/property-locations`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error, "Get city states failed"));
    }
  }
);

export const getIndividualPropertyCalendarBlocksYearOverview = createAsyncThunk(
  "/individual/property/:id/calendar-blocks/year-overview",
  async (
    { id, year, unitId }: GetCalendarBlocksBodyYearOverview,
    { rejectWithValue }
  ) => {
    try {
      const url = `/property/${id}${
        unitId ? `/unit/${unitId}` : ""
      }/calendar-blocks/year-overview`;
      const response = await apiClient.get(url, {
        params: {
          year,
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get calendar blocks year overview failed")
      );
    }
  }
);

export const getIndividualPropertyCalendarBlocks = createAsyncThunk(
  "/individual/property/:id/calendar-blocks",
  async (
    { id, month, year, unitId }: GetCalendarBlocksBody,
    { rejectWithValue, getState }
  ) => {
    try {
      const url = `/property/${id}${
        unitId ? `/unit/${unitId}` : ""
      }/calendar-blocks`;
      const response = await apiClient.get(url, {
        params: {
          year,
          month,
        },
      });
      // Get mappingsMap from Redux state for formatting
      const state = getState() as { ReservationTypeMappings: { mappingsMap: Record<string, unknown> } };
      const mappingsMap = state.ReservationTypeMappings?.mappingsMap || {};
      return { data: response.data.data, mappingsMap };
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get calendar blocks failed")
      );
    }
  }
);

export const updateIndividualCalendarBlock = createAsyncThunk(
  "/individual/update/calendar/block",
  async (
    {
      propertyId,
      blockId,
      body,
    }: { propertyId: string; blockId: string; body: CreateCalendarBlockBody },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await apiClient.put(
        `property/${propertyId}${
          body.unitId ? `/unit/${body.unitId}` : ""
        }/calendar-block/${blockId}`,
        {
          ...body,
          start: body.start ? toDateISOString(body.start) : null,
          end: body.end ? toDateISOString(body.end) : null,
        }
      );
      // Get mappingsMap from Redux state for formatting
      const state = getState() as { ReservationTypeMappings: { mappingsMap: Record<string, unknown> } };
      const mappingsMap = state.ReservationTypeMappings?.mappingsMap || {};
      return { data: response.data.data, mappingsMap };
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Update calendar block failed")
      );
    }
  }
);

export const createIndividualCalendarBlock = createAsyncThunk(
  "/individual/create/calendar/block",
  async (
    { propertyId, body }: { propertyId: string; body: CreateCalendarBlockBody },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await apiClient.post(
        `property/${propertyId}${
          body.unitId ? `/unit/${body.unitId}` : ""
        }/calendar-blocks`,
        {
          ...body,
          start: body.start ? toDateISOString(body.start) : null,
          end: body.end ? toDateISOString(body.end) : null,
        }
      );
      // Get mappingsMap from Redux state for formatting
      const state = getState() as { ReservationTypeMappings: { mappingsMap: Record<string, unknown> } };
      const mappingsMap = state.ReservationTypeMappings?.mappingsMap || {};
      return { data: response.data.data, mappingsMap };
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Create calendar block failed")
      );
    }
  }
);

export const deleteIndividualCalendarBlock = createAsyncThunk(
  "/individual/delete/calendar/block",
  async (
    {
      propertyId,
      blockId,
      unitId,
    }: { propertyId: string; blockId: string; unitId?: string },
    { rejectWithValue }
  ) => {
    try {
      const url = `property/${propertyId}${
        unitId ? `/unit/${unitId}` : ""
      }/calendar-block/${blockId}`;
      const response = await apiClient.delete(url);
      const { status } = response;
      return {
        propertyId,
        blockId,
        unitId,
        status,
        data: response.data.data,
      };
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Delete calendar block failed")
      );
    }
  }
);

export const getPotentialRevenue = createAsyncThunk(
  "get/potential-revenue",
  async (
    { propertyId, start, end, occupants = 2 }: GetPotentialRevenueParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `property/${propertyId}/potential-revenue`,
        {
          params: {
            start: toDateISOString(start),
            end: toDateISOString(end),
            occupants,
          },
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        handleApiError(error, "Get potential revenue failed")
      );
    }
  }
);
